'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { List, Map, HelpCircle } from 'lucide-react';

type Tile = { n: number; x: number; y: number };

export type GameBoardPlayer = {
  id: string;
  name: string;
  /** Photo avatar. Falls back to initials when absent. */
  src?: string;
  /** How the src image fills the puck. 'cover' for square photos, 'contain' for transparent sprites. Defaults to 'cover'. */
  fit?: 'cover' | 'contain';
  initials?: string;
  /** Background for the initials puck. Defaults to ink. */
  color?: string;
  /** Starting tile. The featured player must start on tile 7. */
  tile: number;
};

type Player = GameBoardPlayer & {
  // Bumps when the player wraps back to the start so React re-mounts
  // the avatar and re-runs the pop-in animation.
  spawn: number;
};

// All board coordinates live on this viewBox
const W = 400;
const H = 540;

// Winding stepping-stone path, tile 1 (bottom-right) → tile 14 (top)
const tiles: Tile[] = [
  { n: 1, x: 342, y: 492 },
  { n: 2, x: 342, y: 446 },
  { n: 3, x: 342, y: 400 },
  { n: 4, x: 302, y: 372 },
  { n: 5, x: 258, y: 352 },
  { n: 6, x: 216, y: 332 },
  { n: 7, x: 174, y: 310 },
  { n: 8, x: 136, y: 282 },
  { n: 9, x: 108, y: 244 },
  { n: 10, x: 110, y: 198 },
  { n: 11, x: 150, y: 168 },
  { n: 12, x: 200, y: 144 },
  { n: 13, x: 250, y: 116 },
  { n: 14, x: 290, y: 78 },
];

const YOU_TILE = 7; // matches the 50% progress bar
// Behind band: tiles 1-6, wrap to 1-2
const BEHIND_MAX_TILE = YOU_TILE - 1;
const BEHIND_RESPAWN_TILES = [1, 2];
// Ahead band: tiles 8-13 (14 is the finish flag)
const AHEAD_MAX_TILE = 13;
const AHEAD_RESPAWN_TILE = YOU_TILE + 1;
const TICK_MS = 2200;
const SLIDE_MS = 700;

const pct = (n: number, total: number) => `${(n / total) * 100}%`;

const curvedPath = (() => {
  if (tiles.length < 2) return '';
  let d = `M ${tiles[0].x} ${tiles[0].y}`;
  for (let i = 1; i < tiles.length; i++) {
    const p = tiles[i - 1];
    const c = tiles[i];
    const midX = (p.x + c.x) / 2;
    const midY = (p.y + c.y) / 2;
    d += ` Q ${midX + (c.x - p.x) * 0.1} ${midY - 4} ${c.x} ${c.y}`;
  }
  return d;
})();

function Avatar({ player, isYou }: { player: Player; isYou: boolean }) {
  if (player.src) {
    return (
      <Image
        src={player.src}
        alt={player.name}
        width={isYou ? 48 : 28}
        height={isYou ? 48 : 28}
        className={`w-full h-full ${player.fit === 'contain' ? 'object-contain' : 'object-cover'}`}
      />
    );
  }
  return (
    <div
      className={[
        'w-full h-full flex items-center justify-center text-paper-50 font-display font-black',
        isYou ? 'text-lg' : 'text-[10px]',
      ].join(' ')}
      style={{ backgroundColor: player.color || '#3A3528' }}
    >
      {player.initials}
    </div>
  );
}

type GameBoardProps = {
  /** id of the featured player (large avatar, name in the top bar). Must start on tile 7. */
  youId: string;
  players: GameBoardPlayer[];
  /** Name shown in the in-game top bar, e.g. "Sasha M." */
  headerName: string;
  /** Hand-written note below the board, e.g. "live game" */
  annotation: string;
};

export default function GameBoard({
  youId,
  players: initialPlayers,
  headerName,
  annotation,
}: GameBoardProps) {
  const [players, setPlayers] = useState<Player[]>(() =>
    initialPlayers.map((p) => ({ ...p, spawn: 0 }))
  );
  const [landingTile, setLandingTile] = useState<number | null>(null);
  const landingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTilesRef = useRef<Record<string, number>>(
    Object.fromEntries(initialPlayers.map((p) => [p.id, p.tile]))
  );

  const you = players.find((p) => p.id === youId);

  // Advance one random follower each tick.
  useEffect(() => {
    const id = setInterval(() => {
      setPlayers((prev) => {
        const followers = prev.filter((p) => p.id !== youId);
        if (followers.length === 0) return prev;
        const c = followers[Math.floor(Math.random() * followers.length)];
        const isAhead = c.tile > YOU_TILE;
        const next = c.tile + 1;
        // Cap depends on which band the follower is in.
        const cap = isAhead ? AHEAD_MAX_TILE : BEHIND_MAX_TILE;
        if (next > cap) {
          // Wrap: bump spawn so React re-mounts the avatar with the pop-in
          // animation rather than sliding across the board.
          const newTile = isAhead
            ? AHEAD_RESPAWN_TILE
            : BEHIND_RESPAWN_TILES[
                Math.floor(Math.random() * BEHIND_RESPAWN_TILES.length)
              ];
          return prev.map((p) =>
            p.id === c.id ? { ...p, tile: newTile, spawn: p.spawn + 1 } : p
          );
        }
        return prev.map((p) => (p.id === c.id ? { ...p, tile: next } : p));
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [youId]);

  // Flash the landed-on tile (skip respawn wraps and the leader's tile).
  useEffect(() => {
    let landed: number | null = null;
    for (const p of players) {
      const prev = lastTilesRef.current[p.id];
      if (prev !== undefined && prev !== p.tile) {
        // Treat forward-by-one as a landing; everything else (wraps) ignored.
        if (p.tile === prev + 1 && p.tile !== YOU_TILE) {
          landed = p.tile;
        }
      }
      lastTilesRef.current[p.id] = p.tile;
    }
    if (landed != null) {
      setLandingTile(landed);
      if (landingTimeout.current) clearTimeout(landingTimeout.current);
      landingTimeout.current = setTimeout(() => setLandingTile(null), 750);
    }
    return () => {
      if (landingTimeout.current) {
        clearTimeout(landingTimeout.current);
        landingTimeout.current = null;
      }
    };
  }, [players]);

  return (
    <div className="relative">
      {/* Tape strip */}
      <span
        className="tape z-10"
        style={{ top: '-11px', left: '16%', transform: 'rotate(-4deg)' }}
        aria-hidden="true"
      />

      <div className="relative bg-paper-50 overflow-hidden rounded-[3px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55),_0_8px_20px_-12px_rgba(0,0,0,0.4)] ring-1 ring-ink-900/10 rotate-[0.4deg] hover:rotate-0 transition-transform duration-500">
        {/* In-game top bar */}
        <div className="flex items-center justify-between bg-ink-900 text-paper-50 px-4 pt-2.5 pb-2">
          <span className="font-display text-sm tracking-wide">
            {headerName}
          </span>
          <span className="font-display font-black text-base tabular-nums">
            7
          </span>
        </div>

        {/* Progress bar with leader puck */}
        <div className="bg-ink-900 px-4 pb-3">
          <div className="relative h-1.5 bg-paper-50/15 rounded-full">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-brand-500"
              style={{ width: '50%' }}
            />
            <div
              className="absolute top-1/2 left-[50%] -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full ring-2 ring-paper-50 overflow-hidden bg-paper-100 shadow-md"
              aria-hidden="true"
            >
              {you && <Avatar player={you} isYou={false} />}
            </div>
          </div>
        </div>

        {/* Game board */}
        <div
          className="relative bg-paper-100"
          style={{ aspectRatio: `${W} / ${H}` }}
        >
          {/* SVG layer: grain, topo lines, hills, dashed path, trees */}
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 w-full h-full"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="boardGrain"
                width="180"
                height="180"
                patternUnits="userSpaceOnUse"
              >
                <rect width="180" height="180" fill="transparent" />
                <circle cx="20" cy="40" r="0.6" fill="#3A3528" opacity="0.18" />
                <circle
                  cx="110"
                  cy="90"
                  r="0.5"
                  fill="#3A3528"
                  opacity="0.14"
                />
                <circle
                  cx="60"
                  cy="150"
                  r="0.6"
                  fill="#3A3528"
                  opacity="0.16"
                />
                <circle
                  cx="160"
                  cy="20"
                  r="0.5"
                  fill="#3A3528"
                  opacity="0.12"
                />
              </pattern>
            </defs>

            <rect width={W} height={H} fill="url(#boardGrain)" />

            {/* Topographic contour curves */}
            <g fill="none" stroke="#3A3528" strokeWidth="1" opacity="0.07">
              <path d="M -20 120 C 80 80, 200 160, 320 100 S 460 140, 460 140" />
              <path d="M -20 200 C 80 160, 200 240, 320 180 S 460 220, 460 220" />
              <path d="M -20 320 C 80 280, 200 360, 320 300 S 460 340, 460 340" />
              <path d="M -20 420 C 80 380, 200 460, 320 400 S 460 440, 460 440" />
            </g>

            {/* Soft ground hills along the bottom */}
            <g opacity="0.09">
              <ellipse cx="80" cy="528" rx="170" ry="44" fill="#1A1812" />
              <ellipse cx="340" cy="540" rx="200" ry="52" fill="#1A1812" />
            </g>

            {/* Distant mountain silhouettes, upper-left horizon */}
            <g opacity="0.11" fill="#1A1812">
              <path d="M -30 110 L 40 60 L 100 95 L 150 75 L 200 110 L 200 130 L -30 130 Z" />
            </g>
            <g opacity="0.07" fill="#1A1812">
              <path d="M 60 110 L 130 70 L 180 100 L 240 80 L 290 105 L 290 130 L 60 130 Z" />
            </g>

            {/* Dashed path connecting tiles */}
            <path
              d={curvedPath}
              fill="none"
              stroke="rgb(249 115 22)"
              strokeWidth="2.5"
              strokeDasharray="5 7"
              strokeLinecap="round"
              opacity="0.4"
            />

            {/* Decorative trees */}
            <Tree x={195} y={248} />
            <Tree x={62} y={388} />
            <Tree x={358} y={208} />
            <Tree x={262} y={428} small />
            <Tree x={40} y={170} small />

            {/* "Start" marker, bottom-right */}
            <g transform="translate(370, 504) rotate(-8)" opacity="0.7">
              <text
                style={{ fontFamily: 'var(--font-caveat), cursive' }}
                fontSize="14"
                fill="#3A3528"
              >
                start
              </text>
            </g>
          </svg>

          {/* HTML layer: numbered tiles, avatars, finish flag */}
          <div className="absolute inset-0">
            {tiles.map((t) => {
              const isYou = t.n === YOU_TILE;
              const flashing = t.n === landingTile && !isYou;
              return (
                <div
                  key={t.n}
                  className="absolute"
                  style={{
                    left: pct(t.x, W),
                    top: pct(t.y, H),
                    transform: 'translate(-50%, -50%)',
                    zIndex: isYou ? 4 : 2,
                  }}
                >
                  <div
                    className={[
                      'flex items-center justify-center rounded-full font-display font-black tabular-nums',
                      isYou
                        ? 'w-9 h-9 text-sm bg-brand-600 text-white shadow-[0_4px_0_-1px_rgb(var(--brand-800))]'
                        : 'w-7 h-7 text-[11px] bg-brand-500 text-ink-900 shadow-[0_3px_0_-1px_rgb(var(--brand-700))]',
                      flashing ? 'tile-land' : '',
                    ].join(' ')}
                  >
                    {t.n}
                  </div>
                </div>
              );
            })}

            {/* Avatars — animated */}
            {players.map((p) => {
              const tile = tiles.find((t) => t.n === p.tile);
              if (!tile) return null;
              const isYou = p.id === youId;
              const offsetX = isYou ? 0 : 24;
              const offsetY = isYou ? -28 : 2;
              const x = tile.x + offsetX;
              const y = tile.y + offsetY;

              // Respawned instances pop in; everything else slides.
              const isFreshSpawn = p.spawn > 0;

              return (
                <div
                  key={`${p.id}-${p.spawn}`}
                  className={[
                    'absolute will-change-transform',
                    isFreshSpawn ? 'avatar-pop' : '',
                  ].join(' ')}
                  style={{
                    left: pct(x, W),
                    top: pct(y, H),
                    transform: 'translate(-50%, -50%)',
                    // Always slide on top/left changes. Respawn jumps are
                    // handled by the React key changing (remount), so the
                    // transition never fires across a wrap.
                    transition: `left ${SLIDE_MS}ms cubic-bezier(0.34, 1.2, 0.64, 1), top ${SLIDE_MS}ms cubic-bezier(0.34, 1.2, 0.64, 1)`,
                    zIndex: isYou ? 6 : 3,
                  }}
                >
                  <div
                    className={[
                      'rounded-full overflow-hidden bg-paper-50',
                      isYou
                        ? 'w-12 h-12 ring-[3px] ring-brand-500 shadow-[0_8px_18px_-6px_rgba(0,0,0,0.55)]'
                        : 'w-7 h-7 ring-2 ring-paper-50 shadow-[0_4px_10px_-4px_rgba(0,0,0,0.45)]',
                    ].join(' ')}
                  >
                    <Avatar player={p} isYou={isYou} />
                  </div>
                </div>
              );
            })}

            {/* Finish flag, anchored just above the leader tile */}
            <div
              className="absolute"
              style={{
                left: pct(346, W),
                top: pct(40, H),
                transform: 'translate(-50%, -50%) rotate(-6deg)',
              }}
              aria-hidden="true"
            >
              <FinishFlag />
            </div>
          </div>
        </div>

        {/* Bottom tab bar — subtle, illustrative only (not a faithful UI screenshot) */}
        <div className="flex items-center justify-around bg-paper-200/80 px-2 py-1.5 border-t border-ink-900/5">
          <TabIcon icon={HelpCircle} label="Question" />
          <TabIcon icon={Map} label="Map" active />
          <TabIcon icon={List} label="Leaderboard" />
        </div>
      </div>

      {/* Hand-written annotation */}
      <p
        className="absolute -bottom-8 right-3 font-hand text-brand-600 text-xl -rotate-[4deg] pointer-events-none"
        aria-hidden="true"
      >
        {annotation}
      </p>
    </div>
  );
}

function TabIcon({
  icon: Icon,
  label,
  active = false,
}: {
  icon: typeof Map;
  label: string;
  active?: boolean;
}) {
  return (
    <div
      aria-label={label}
      className={[
        'flex items-center justify-center transition-colors',
        active
          ? 'px-4 py-0.5 rounded-full border border-ink-900/30 text-ink-900'
          : 'px-2 py-0.5 text-ink-400',
      ].join(' ')}
    >
      <Icon className="w-4 h-4" strokeWidth={2.2} />
    </div>
  );
}

function Tree({
  x,
  y,
  small = false,
}: {
  x: number;
  y: number;
  small?: boolean;
}) {
  const s = small ? 0.7 : 1;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} opacity="0.85">
      <ellipse cx="0" cy="14" rx="11" ry="2.5" fill="#1A1812" opacity="0.15" />
      <rect x="-1.6" y="-2" width="3.2" height="14" fill="#5C5443" rx="1" />
      <path d="M -12 -2 L 12 -2 L 0 -18 Z" fill="#3A3528" opacity="0.9" />
      <path d="M -10 -10 L 10 -10 L 0 -24 Z" fill="#3A3528" opacity="0.9" />
      <path d="M -8 -18 L 8 -18 L 0 -30 Z" fill="#3A3528" opacity="0.95" />
    </g>
  );
}

function FinishFlag() {
  return (
    <svg
      width="34"
      height="42"
      viewBox="0 0 34 42"
      fill="none"
      className="drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]"
    >
      <rect x="2" y="2" width="2" height="38" fill="#1A1812" rx="1" />
      <path
        d="M 4 4 L 30 4 L 24 12 L 30 20 L 4 20 Z"
        fill="rgb(249 115 22)"
        stroke="#1A1812"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <rect x="4" y="4" width="6" height="8" fill="#1A1812" />
      <rect x="10" y="12" width="6" height="8" fill="#1A1812" />
    </svg>
  );
}
